import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { OrdersService } from "services/orders";
import { OrderStatisticsResult } from "services/orders/models/OrdersTypes";
import { useTranslation } from "react-i18next";

const FALLBACK_COLORS = ['#667eea', '#f093fb', '#11998e', '#fa709a', '#ee0979', '#fbc7d4', '#4facfe', '#00f2fe'];

const OrderStatisticsChart = () => {
  const [statistics, setStatistics] = useState<OrderStatisticsResult[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await OrdersService.getOrderStatistics();
      if (!response.hasError && response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error("Error fetching order statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColorForIndex = (index: number): string => {
    return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  const renderCustomLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, orderStatusName, percent } = props;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const textAnchor = isRTL
      ? (x > cx ? 'end' : 'start')
      : (x > cx ? 'start' : 'end');

    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        dominantBaseline="central"
        style={{
          fontSize: '13px',
          fontWeight: 500,
          fill: '#2c3e50',
          direction: isRTL ? 'rtl' : 'ltr',
        }}
      >
        {`${orderStatusName}: ${((percent ?? 0) * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <div
        className="card"
        style={{
          backgroundColor: 'white',
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          minHeight: '400px',
        }}
      >
        <div className="d-flex justify-content-center align-items-center h-100" style={{ minHeight: 400 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (statistics.length === 0) {
    return (
      <div
        className="card"
        style={{
          backgroundColor: 'white',
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        }}
      >
        <div className="text-center text-muted p-4">
          {t("Common:noData")}
        </div>
      </div>
    );
  }

  return (
    <div
      className="card"
      style={{
        backgroundColor: 'white',
        border: 'none',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
      }}
    >
      <div className="card-header" style={{ backgroundColor: 'white', border: 'none', paddingTop: '1.5rem', paddingBottom: '1rem' }}>
        <h5 className="card-title mb-0 fw-bold" style={{ color: '#2c3e50', fontSize: '1.25rem' }}>
          {t("Dashboard:orderStatistics")}
        </h5>
      </div>
      <div className="card-body p-4" style={{ backgroundColor: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={statistics as any[]}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={120}
              fill="#8884d8"
              paddingAngle={3}
              dataKey="totalOrders"
              label={renderCustomLabel}
              labelLine={{
                stroke: '#cbd5e0',
                strokeWidth: 1,
              }}
              style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            >
              {statistics.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorForIndex(index)}
                  stroke="white"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                direction: isRTL ? 'rtl' : 'ltr',
              }}
              formatter={(value: any, _name: any, props: any) => [
                value,
                props.payload.orderStatusName
              ]}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              wrapperStyle={{ direction: isRTL ? 'rtl' : 'ltr' }}
              formatter={(value, entry: any) => (
                <span style={{ color: '#2c3e50', fontWeight: 500 }}>
                  {entry.payload.orderStatusName} ({entry.payload.totalOrders})
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OrderStatisticsChart;